import * as Yup from 'yup'
import { set, getHours, getMinutes, parseISO, isToday, startOfDay } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

import { formatBuildingsForFilter } from '../../../../reducers/buildingSelectorReducer'

import { MAX_TEXT_LENGTH } from './constants'

export const combineDateTime = (date, time, timezone = 'America/Chicago') => {
  const combined = set(new Date(date), {
    hours: getHours(new Date(time)),
    minutes: getMinutes(new Date(time)),
    seconds: 0,
    milliseconds: 0,
  })

  return fromZonedTime(combined, timezone).getTime()
}

export const getInitialScheduledDate = (scheduledAt, timezone = 'America/Chicago') => {
  if (!scheduledAt) return null
  const date = typeof scheduledAt === 'string' ? parseISO(scheduledAt) : new Date(scheduledAt)
  return toZonedTime(date, timezone)
}

export const getInitialScheduledTime = (scheduledAt, timezone = 'America/Chicago') => {
  if (!scheduledAt) return null
  const date = typeof scheduledAt === 'string' ? parseISO(scheduledAt) : new Date(scheduledAt)
  return toZonedTime(date, timezone)
}

export const getMinTimeForSchedule = (scheduledDate, timezone = 'America/Chicago') => {
  const nowInTimezone = toZonedTime(new Date(), timezone)
  const scheduledDateInTimezone = toZonedTime(new Date(scheduledDate), timezone)

  return isToday(scheduledDateInTimezone) ? nowInTimezone : startOfDay(scheduledDateInTimezone)
}

export const filterGroupsForMultipleBuildingSelection = (groups) => {
  if (!groups) {
    return []
  }

  return groups.filter((group) => ['Community Feed', 'Marketplace'].includes(group.text))
}

const FIELD_PLACEHOLDER_FALLBACK = {
  groups: 'Select groups',
  buildings: 'Select buildings',
  tags: 'Select tags',
}

export const renderFieldPlaceholder = (values, type) => {
  const isFallbackNeeded = values.length === 0
  return isFallbackNeeded ? FIELD_PLACEHOLDER_FALLBACK[type] : values.join(', ')
}

export const getBuildingsOptions = (buildings, isMultipleBuildingSelection) => {
  const formattedBuildings = formatBuildingsForFilter(buildings)
  if (isMultipleBuildingSelection) {
    return [
      {
        id: -1,
        text: 'All Buildings',
        avatar: 'building',
        selectAll: true,
      },
      ...formattedBuildings,
    ]
  }

  return formattedBuildings
}

export const getTagsOptions = (buildings) => {
  if (!buildings) {
    return []
  }

  const tagsSet = [...new Set(buildings.flatMap((building) => building.tags))]
  const tagsOptions = tagsSet.map((tag, i) => ({ text: tag, id: i }))
  return [
    {
      id: -1,
      text: 'All Tags',
      selectAll: true,
    },
    ...tagsOptions,
  ]
}

export const getGroupsOptions = (isMultipleBuildingSelection, groups) => {
  const options = [
    {
      id: -1,
      text: 'All Groups',
      selectAll: true,
    },
  ]

  if (isMultipleBuildingSelection) {
    options.push(...filterGroupsForMultipleBuildingSelection(groups))
  } else {
    options.push(...groups)
  }

  return options
}

export const validationSchema = (buildingTimezone = 'America/Chicago') =>
  Yup.object().shape({
    title: Yup.string().trim().required('Required'),
    message: Yup.string()
      .trim()
      .max(MAX_TEXT_LENGTH, `Max ${MAX_TEXT_LENGTH} characters`)
      .required('Required'),
    tags: Yup.array(),
    buildings: Yup.array(),
    groups: Yup.array().required('Required'),
    isScheduled: Yup.boolean(),
    isPublished: Yup.boolean(),
    scheduledDate: Yup.date()
      .nullable()
      .when(['isScheduled', 'isPublished'], {
        is: (isScheduled, isPublished) => isScheduled && !isPublished,
        then: (schema) =>
          schema
            .required('Delivery date is required')
            .min(toZonedTime(new Date(), buildingTimezone), 'Date must be in the future'),
      }),
    scheduledTime: Yup.date()
      .nullable()
      .when(['isScheduled', 'isPublished'], {
        is: (isScheduled, isPublished) => isScheduled && !isPublished,
        then: (schema) => schema.required('Delivery time is required'),
      }),
  })
